/**
 * SessionStorage-backed "next URL after auth" capture.
 *
 * Used to preserve a destination across the Turnkey login modal AND any
 * onboarding redirect, so a user clicking a gift link → "Sign in to claim"
 * lands back on the claim page after auth + onboarding.
 *
 * SessionStorage (vs localStorage):
 *  - Survives the in-tab Turnkey modal + the post-auth navigation chain.
 *  - Clears on tab close, so we don't surprise users who close mid-flow.
 *  - Not shared across tabs — exactly what we want for an in-tab auth flow.
 *
 * The value is intentionally a relative path only; we strip any absolute
 * scheme/host on read to prevent open-redirect attacks.
 */

import { isValidLocale } from '@/i18n/config';

const STORAGE_KEY = 'morfi:auth_next';

/**
 * Paths that must NEVER be captured as the post-auth destination — landing on
 * any of these would either loop the user back into the auth flow or drop them
 * on a transitional page. Compared as prefix or exact match against the
 * locale-stripped pathname.
 */
const NEVER_CAPTURE_PREFIXES: readonly string[] = ['/auth/', '/onboarding', '/login', '/signup'];

function isSafeRelativePath(value: string): boolean {
  // Must start with a single forward slash and not be a protocol-relative URL.
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  // Reject anything that smells like a scheme (`javascript:`, `http:`, etc.)
  if (/^\/[a-z]+:/i.test(value)) return false;
  return true;
}

/**
 * Strip the leading `/{locale}` segment from a pathname if present.
 * `next-intl`'s locale-aware `router.push()` re-prepends the active locale at
 * consume time, so the stored value MUST be locale-less. Callers pass paths
 * derived from `window.location.pathname` (which always includes the locale).
 */
function stripLocalePrefix(pathname: string): string {
  const match = pathname.match(/^\/([^/]+)(\/.*)?$/);
  if (match === null) return pathname;
  const firstSegment = match[1] ?? '';
  if (!isValidLocale(firstSegment)) return pathname;
  // Drop the leading `/{locale}`; if nothing follows, return `/`.
  return match[2] ?? '/';
}

function shouldSkipCapture(pathWithoutLocale: string): boolean {
  // The bare root is the marketing landing page pre-launch (and == `/markets`
  // post-launch). Capturing it as the post-auth destination sends a
  // freshly-onboarded user back to the landing page instead of into the app;
  // skip it so the redirect falls through to the default (`/markets`).
  if (pathWithoutLocale === '/') return true;
  return NEVER_CAPTURE_PREFIXES.some(
    (prefix) => pathWithoutLocale === prefix || pathWithoutLocale.startsWith(`${prefix}/`),
  );
}

/**
 * Capture the page the user is currently on as the post-auth destination.
 *
 * Call this synchronously at the moment a "Sign in" / "Log in" / "Sign up"
 * trigger fires — before any modal, redirect, or async work begins. The
 * captured value is locale-stripped (the consume-side router re-prepends the
 * active locale) and includes `?search` so query state survives the round
 * trip. Hash is intentionally omitted — Turnkey's redirect flow uses the URL
 * hash for OIDC tokens, so a captured fragment would be clobbered anyway.
 *
 * Does nothing on the server, when sessionStorage is blocked, or when the
 * current path is a transitional auth/onboarding surface.
 */
export function captureReturnToAfterAuth(): void {
  if (typeof window === 'undefined') return;
  const pathname = stripLocalePrefix(window.location.pathname);
  if (shouldSkipCapture(pathname)) return;
  const target = pathname + window.location.search;
  setNextAfterAuth(target);
}

/**
 * Store a destination to navigate to after auth (and any onboarding) completes.
 * Pass a locale-less path like `/gifts/abc123/claim` — the locale-aware router
 * will prepend the active locale at consume time.
 */
export function setNextAfterAuth(path: string): void {
  if (typeof window === 'undefined') return;
  if (!isSafeRelativePath(path)) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    // SILENT-CATCH: sessionStorage may be blocked (privacy mode); the auth
    // flow falls back to its default destination.
  }
}

/**
 * Read and clear the stored next URL. Returns `null` if nothing is set or the
 * stored value isn't a safe relative path.
 */
export function consumeNextAfterAuth(): string | null {
  if (typeof window === 'undefined') return null;
  let value: string | null = null;
  try {
    value = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // SILENT-CATCH: sessionStorage may be blocked (privacy mode); fall back
    // to the auth flow's default destination instead of crashing.
    return null;
  }
  if (value === null) return null;
  return isSafeRelativePath(value) ? value : null;
}

/**
 * Peek without clearing. Used by intermediate route guards (e.g. the
 * onboarding page) that want to know there's a destination but defer
 * consumption until the actual hand-off.
 */
export function peekNextAfterAuth(): string | null {
  if (typeof window === 'undefined') return null;
  let value: string | null = null;
  try {
    value = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // SILENT-CATCH: sessionStorage may be blocked (privacy mode); peek is
    // best-effort and the caller treats null as "no stored destination".
    return null;
  }
  if (value === null) return null;
  return isSafeRelativePath(value) ? value : null;
}
